import { useState } from 'react';
import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import type { Comment, Step } from './types';
import { commentDate } from './commentDate';
import { useWikiHtml } from './useWikiHtml';

// Top-level comments shown before the "show more" reveal (parity with the
// vanilla COMMENTS_SHOWN); anything past the server cap links out to iFixit.
const COMMENTS_SHOWN = 3;

/**
 * Read-only step comments under the step body in the single-step embed. Collapsed
 * behind a "Comments (N)" toggle; when open, the first few render inline with the
 * rest behind a "show more", and a count beyond the server cap links to the full
 * step on iFixit. Parity with the comment block of embed.v5.steps.ejs — read-only
 * (no compose/reply), since the embed never authored comments.
 */
export function StepComments({ step, className }: { step: Step; className?: string }) {
   const comments = step.comments ?? [];
   const total = step.comment_count ?? comments.length;
   const [open, setOpen] = useState(false);
   const [showExtra, setShowExtra] = useState(false);
   // Comment bodies are wiki HTML: force link targets once the list is shown.
   // Keyed on open/expand so the effect re-runs when the list actually mounts.
   const listRef = useWikiHtml<HTMLUListElement>(`${open}:${showExtra}`);

   if (comments.length === 0) {
      return null;
   }

   const visible = showExtra ? comments : comments.slice(0, COMMENTS_SHOWN);
   const hiddenCount = comments.length - visible.length;
   const loaded = comments.reduce((sum, c) => sum + 1 + (c.replies?.length ?? 0), 0);
   // Only build the outbound link when we know the guide's URL; without it a
   // bare `#s3` would just open a blank tab pointed at the embed itself.
   const stepUrl = App.guideViewUrl && step.stepid ? `${App.guideViewUrl}#s${step.stepid}` : '';

   return (
      <section className={className} aria-label={_js('Comments')}>
         <button
            type="button"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            className="flex w-full items-center gap-2 text-sm font-semibold text-gray-800"
         >
            <FaIcon name="comment" className="text-gray-500" aria-hidden />
            <span>{_js('Comments (%1)', String(total))}</span>
            <FaIcon
               name="chevron-down"
               className={cn('text-gray-400 transition-transform', open && 'rotate-180')}
               aria-hidden
            />
         </button>

         {open && (
            <div className="mt-3 rounded-lg bg-gray-100 px-4 py-1">
               <ul ref={listRef} className="m-0 list-none p-0">
                  {visible.map(comment => (
                     <CommentItem key={comment.commentid} comment={comment} />
                  ))}
               </ul>

               {(hiddenCount > 0 || (total > loaded && stepUrl)) && (
                  <div className="flex items-center gap-4 py-3">
                     {hiddenCount > 0 && (
                        <button
                           type="button"
                           onClick={() => setShowExtra(true)}
                           className="text-sm font-bold text-brand-500 hover:underline"
                        >
                           {_js('Show %1 more', String(hiddenCount))}
                        </button>
                     )}
                     {total > loaded && stepUrl && (
                        <a
                           href={stepUrl}
                           target="_blank"
                           rel="noreferrer"
                           className="text-sm font-bold text-brand-500 hover:underline"
                        >
                           {_js('See all %1 comments on iFixit', String(total))}
                        </a>
                     )}
                  </div>
               )}
            </div>
         )}
      </section>
   );
}

function CommentItem({ comment, isReply = false }: { comment: Comment; isReply?: boolean }) {
   const author = comment.author ?? null;
   const username = author?.username || _js('Anonymous');
   const avatar =
      (author?.image?.mini as string | undefined) ??
      (author?.image?.standard as string | undefined) ??
      (author?.image?.thumbnail as string | undefined) ??
      '';
   const replies = comment.replies ?? [];

   return (
      <li className={isReply ? '[&+li]:mt-3' : 'py-3.5'}>
         <div className="flex items-start gap-2.5">
            {avatar ? (
               <img
                  className="size-7 flex-none rounded-full object-cover"
                  src={avatar}
                  alt=""
                  width={28}
                  height={28}
                  loading="lazy"
               />
            ) : (
               <span className="flex size-7 flex-none items-center justify-center rounded-full bg-gray-200 text-gray-500">
                  <FaIcon name="user" className="text-xs" aria-hidden />
               </span>
            )}
            <div className="min-w-0 flex-1">
               <div className="mb-0.5 flex items-baseline gap-2">
                  {author?.url ? (
                     <a
                        href={author.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm font-bold text-brand-500"
                     >
                        {username}
                     </a>
                  ) : (
                     <span className="text-sm font-bold text-gray-800">{username}</span>
                  )}
                  <span className="text-xs text-gray-500">{commentDate(comment.date)}</span>
               </div>
               {comment.text_rendered && (
                  <div
                     className="break-words text-sm leading-normal text-gray-700 [&_a]:text-brand-500 [&_a]:underline [&_p:first-child]:mt-0 [&_p:last-child]:mb-0"
                     // Rendered by the guide API's wiki renderer (server-sanitized HTML).
                     dangerouslySetInnerHTML={{ __html: comment.text_rendered }}
                  />
               )}
            </div>
         </div>

         {replies.length > 0 && (
            <ul className="m-0 mt-3 list-none p-0 pl-[38px]">
               {replies.map(reply => (
                  <CommentItem key={reply.commentid} comment={reply} isReply />
               ))}
            </ul>
         )}
      </li>
   );
}
